<template>
	<div
		class="c-Radio"
		:class="RadioClassObj">
		<button
			v-for="(opt, index) in radioItems"
			:key="index"
			class="c-Radio__btn"
			:disabled="disabled"
			@mousedown.prevent
			@keyup.enter="handleKeyup(`${radioId}${opt.label}${opt.value}`)"
			@keyup.space="handleKeyup(`${radioId}${opt.label}${opt.value}`)">
			<input
				:id="`${radioId}${opt.label}${opt.value}`"
				v-model="value"
				class="c-Radio__input"
				type="radio"
				:value="opt.value"
				:disabled="disabled"
				@change="selectOption">
			<label
				class="c-Radio__label"
				:for="`${radioId}${opt.label}${opt.value}`">
				<span>{{ opt.label }}</span>
			</label>
		</button>
	</div>
</template>

<script>
export default {
	name: 'Radio',
	props: {
		/**
     * The Radio group options
     */
		radioItems: {
			type: Array,
			required: true,
			default: () => [],
			validator: (data) => {
				const conditions = []
				data.forEach((item) => {
					conditions.push(Object.keys(item).indexOf('label'))
					conditions.push(Object.keys(item).indexOf('value'))
				})

				return data.length === 0 || conditions.every(cond => cond > -1)
			}
		},
		/**
     * The Radio data variable
     */
		data: {
			type: [Boolean, String, Number],
			required: false,
			default: () => ''
		},
		/**
     * If the Radio input is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * Controls how the Radio items are arranged on the container
     * @values flex-start, flex-end, center, space-between, space-around, space-evenly, equal-spacing-align-left, equal-spacing-align-right
     */
		itemsAlignment: {
			type: String,
			required: false,
			default: () => 'flex-start',
			validator(type) {
				return [
					'flex-start',
					'flex-end',
					'center',
					'space-between',
					'space-around',
					'space-evenly',
					'equal-spacing-align-left',
					'equal-spacing-align-right'
				].includes(type);
			},
		}
	},
	data () {
		return {
			value: '',
			radioId: this.randomizeId()
		}
	},
	computed: {
		RadioClassObj () {
			return {
				[`${this.itemsAlignment}`]: true
			}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				this.value = newVal
			},
			immediate: true
		}
	},
	methods: {
		randomizeId () {
			return `radioid${Math.floor(Math.random() * 100000000000000000000)}`
		},
		handleKeyup (id) {
			document.querySelector(`#${id}`).click()
		},
		selectOption () {
			/**
       * Triggers when Radio changes it's value
       * @event change
       * @property {boolean, string} value new value set
       */
			this.$emit('change', this.value)
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-Radio {
  $label-font-size: 16px;

  display: flex;
	align-items: center;

	&.flex-start {
		justify-content: flex-start;

		.c-Radio__btn {
			margin-right: calc(#{$base-margin} * 2);

			&:last-child {
				margin-right: 0;
			}
		}
	}

	&.flex-end {
		justify-content: flex-end;

		.c-Radio__btn {
			margin-right: calc(#{$base-margin} * 2);

			&:last-child {
				margin-right: 0;
			}
		}
	}

	&.center {
		justify-content: center;

		.c-Radio__btn {
			margin-right: calc(#{$base-margin} * 2);

			&:last-child {
				margin-right: 0;
			}
		}
	}

	&.space-between {
		justify-content: space-between;
	}

	&.space-around {
		justify-content: space-around;
	}

	&.space-evenly {
		justify-content: space-evenly;
	}

	&.equal-spacing-align-left {
		.c-Radio__btn {
			width: 100%;
		}
	}

	&.equal-spacing-align-right {
		.c-Radio__btn {
			width: 100%;

			& label {
				display: flex;
				justify-content: flex-end;
			}

			& span {
				width: unset;
			}
		}
	}

  &__btn {
    height: 50px;
    border: none;
    outline: none;
    background-color: transparent;
    margin: 0;
    padding: 0;
		position: relative;
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &__btn:disabled .c-Radio__label {
    color: $un-n500 !important;

		span {
			cursor: not-allowed;
		}

    span::before {
      border-color: $un-n500 !important;
      box-shadow: none !important;
      background-color: $un-n100 !important;
    }

    span::after {
      background-color: $un-n500 !important;
    }
  }

  .c-Radio__btn:focus .c-Radio__label {
    color: $un-blue;

    span::before {
      border-color: $un-blue;
      box-shadow: inset 0 0 0 1px $un-blue;
    }
  }

  &__input {
    width: 0;
    height: 0;
    opacity: 0;
    visibility: hidden;
    position: absolute;
    top: 0;
    left: 0;
  }

  &__label {
    font-size: $label-font-size;
    color: $un-n700;
    text-align: left;
    width: 100%;
    height: 100%;

    span {
      cursor: pointer;
      padding: calc(#{$base-padding / 2}) 0;
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      outline: none;
      position: relative;

      &::-moz-focus-inner {
        border: 0;
      }

      &::before {
        content: '';
        position: relative;
        width: 20px;
        height: 20px;
        border: 1px solid $un-n900;
        border-radius: 20px;
        background-color: $un-white;
        display: block;
        margin-right: calc(#{$base-margin} / 2);
      }

      &::after {
        content: '';
        position: relative;
        width: 14px;
        height: 14px;
        border-radius: 14px;
        background-color: $un-blue;
        visibility: hidden;
        opacity: 0;
        position: absolute;
        top: 18px;
        left: 4px;
        transition: all $default-time $default-easing;
      }
    }
  }

  .c-Radio__input:checked + .c-Radio__label span {
    &::after {
      visibility: visible;
      opacity: 1;
    }
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/@unbabel/ui/src/components/Radio/Radio.vue